import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import "./VoucherEntry.css";
import BillPopup from "./BillPopup";
import { Api } from "../Api/Api";

export default function VoucherEntry() {
  const [companies, setCompanies] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [showPopup, setShowPopup] = useState(false);

  // --- Helpers ---
  function formatDateForDB(dateObj) {
    const d = new Date(dateObj);
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
      2,
      "0"
    )}-${String(d.getDate()).padStart(2, "0")}`;
  }

  function formatDateForUI(dateStr) {
    const d = new Date(dateStr);
    return `${String(d.getDate()).padStart(2, "0")}/${String(
      d.getMonth() + 1
    ).padStart(2, "0")}/${d.getFullYear()}`;
  }

  const [formData, setFormData] = useState({
    companyId: "",
    companyName: "",
    companyGST: "",
    companyEmail: "",
    companyAddress: "",

    buyerId: "",
    buyerName: "",
    buyerAddress: "",

    invoiceNo: "",
    date: formatDateForDB(new Date()), // ✅ store in DB format (YYYY-MM-DD)
    paymentType: "",
    transport: "",
    bundles: "",
    deliveryPlace: "",
    llrNo: "",
  });

  // --- Fetch companies + buyers ---
  useEffect(() => {
    axios
      .get(`${Api}company/get.php`)
      .then((res) => setCompanies(res.data || []))
      .catch((err) => console.error("Company fetch error:", err));

    axios
      .get(`${Api}buyer/get.php`)
      .then((res) => setBuyers(res.data || []))
      .catch((err) => console.error("Buyer fetch error:", err));
  }, []);

  // --- Memo selections ---
  const selectedCompany = useMemo(
    () => companies.find((c) => String(c.id) === String(formData.companyId)),
    [companies, formData.companyId]
  );

  const selectedBuyer = useMemo(
    () => buyers.find((b) => String(b.id) === String(formData.buyerId)),
    [buyers, formData.buyerId]
  );

  // --- Auto-fill on company/buyer select ---
  useEffect(() => {
    if (selectedCompany) {
      setFormData((p) => ({
        ...p,
        companyName: selectedCompany.company_name || "",
        companyGST: selectedCompany.gst_number || "",
        companyEmail: selectedCompany.email || "",
        companyAddress:
          selectedCompany.address ||
          `${selectedCompany.address_line1 || ""} ${
            selectedCompany.address_line2 || ""
          }`.trim(),
      }));
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (selectedBuyer) {
      setFormData((p) => ({
        ...p,
        buyerName: selectedBuyer.company_name || "",
        buyerAddress:
          selectedBuyer.address ||
          `${selectedBuyer.address_line1 || ""} ${
            selectedBuyer.address_line2 || ""
          }`.trim(),
      }));
    }
  }, [selectedBuyer]);

  // --- Handle input changes ---
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === "date" ? formatDateForDB(new Date(value)) : value,
    }));
  };

  // --- Proceed to Bill ---
  const handleNext = (e) => {
    e.preventDefault();
    if (!formData.companyId || !formData.buyerId) {
      alert("Please select Company and Buyer.");
      return;
    }
    setShowPopup(true);
  };

  // --- Reset form ---
  const resetForm = () => {
    const today = new Date();
    setFormData({
      companyId: "",
      companyName: "",
      companyGST: "",
      companyEmail: "",
      companyAddress: "",

      buyerId: "",
      buyerName: "",
      buyerAddress: "",

      invoiceNo: "",
      date: formatDateForDB(today), // ✅ reset to DB format
      paymentType: "",
      transport: "",
      bundles: "",
      deliveryPlace: "",
      llrNo: "",
    });
  };

  return (
    <div className="voucher-container">
      <h2 className="text-2xl font-semibold mb-6 text-center">Voucher Entry</h2>

      <form onSubmit={handleNext}>
        <div className="voucher-grid">
          {/* Left */}
          <div>
            <div className="form-group">
              <label>Company:</label>
              <select
                name="companyId"
                value={formData.companyId}
                onChange={handleChange}
              >
                <option value="">Select Company</option>
                {companies.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.company_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Buyer Company:</label>
              <select
                name="buyerId"
                value={formData.buyerId}
                onChange={handleChange}
              >
                <option value="">Select Buyer</option>
                {buyers.map((b) => (
                  <option key={b.id} value={b.id}>
                    {b.company_name}
                  </option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Company GST:</label>
              <input type="text" value={formData.companyGST} readOnly />
            </div>

            <div className="form-group">
              <label>Company Email:</label>
              <input type="text" value={formData.companyEmail} readOnly />
            </div>

            <div className="form-group">
              <label>Company Address:</label>
              <textarea rows={3} value={formData.companyAddress} readOnly />
            </div>

            <div className="form-group">
              <label>Buyer Address:</label>
              <textarea rows={3} value={formData.buyerAddress} readOnly />
            </div>
          </div>

          {/* Right */}
          <div>
            <div className="form-group">
              <label>Date:</label>
              <input
                type="date"
                name="date"
                value={formData.date} // ✅ YYYY-MM-DD works with <input type="date">
                onChange={handleChange}
              />
              <small>Saved as {formatDateForUI(formData.date)}</small>
            </div>

            <div className="form-group">
              <label>Transport:</label>
              <input
                type="text"
                name="transport"
                value={formData.transport}
                onChange={handleChange}
                placeholder="e.g., Angalamman Transport"
              />
            </div>

            <div className="form-group">
              <label>No. of Bundles:</label>
              <input
                type="number"
                name="bundles"
                value={formData.bundles}
                onChange={handleChange}
                min="0"
              />
            </div>

            <div className="form-group">
              <label>Delivery Place:</label>
              <input
                type="text"
                name="deliveryPlace"
                value={formData.deliveryPlace}
                onChange={handleChange}
                placeholder="e.g., Dharapuram"
              />
            </div>

            <div className="form-group">
              <label>LLR No:</label>
              <input
                type="text"
                name="llrNo"
                value={formData.llrNo}
                onChange={handleChange}
                placeholder="Lorry Receipt No."
              />
            </div>

            <div className="form-actions">
              <button type="submit">Next (Bill)</button>
            </div>
          </div>
        </div>
      </form>

      {showPopup && (
        <BillPopup
          open={showPopup}
          onClose={() => {
            setShowPopup(false);
            resetForm();
          }}
          header={formData}
        />
      )}
    </div>
  );
}
